import { useEffect, useState } from "preact/hooks"
import { useRange } from "@nosto/search-js/preact/hooks"
import DualRange from "@/elements/DualRange/DualRange"
import { SearchStatsFacet } from "@nosto/nosto-js/client"
import styles from "./RangeFacet.module.css"
import { cl } from "@nosto/search-js/utils"

type Props = {
  facet: SearchStatsFacet
  /** Whether the facet is initially expanded (default: false) */
  defaultActive?: boolean
}

export default function RangeFacet({ facet, defaultActive = false }: Props) {
  const { min, max, range, updateRange } = useRange(facet.id)
  const [active, setActive] = useState(defaultActive)

  const lowValue = range[0] ?? min
  const highValue = range[1] ?? max

  // Local input state so typing doesn't fire a search on every keystroke;
  // committed on blur / Enter. Kept in sync when the range changes elsewhere.
  const [minInput, setMinInput] = useState(String(lowValue))
  const [maxInput, setMaxInput] = useState(String(highValue))

  useEffect(() => {
    setMinInput(String(range[0] ?? min))
    setMaxInput(String(range[1] ?? max))
  }, [range, min, max])

  const commit = (nextLow: number, nextHigh: number) => {
    updateRange([nextLow <= min ? undefined : nextLow, nextHigh >= max ? undefined : nextHigh])
  }

  const commitMin = () => {
    const parsed = parseFloat(minInput)
    if (isNaN(parsed)) {
      setMinInput(String(lowValue))
      return
    }
    commit(Math.min(Math.max(parsed, min), highValue), highValue)
  }

  const commitMax = () => {
    const parsed = parseFloat(maxInput)
    if (isNaN(parsed)) {
      setMaxInput(String(highValue))
      return
    }
    commit(lowValue, Math.max(Math.min(parsed, max), lowValue))
  }

  return (
    <li className={cl(styles.facet, active && styles.active)}>
      <button
        className={styles.header}
        onClick={() => setActive(!active)}
        aria-controls={`${facet.id}-range-menu`}
        aria-expanded={active}
        aria-label={`${active ? "Collapse" : "Expand"} ${facet.name} range filter`}
        type="button"
      >
        <span className={styles.title}>{facet.name}</span>
        <span className={styles.toggleIcon} aria-hidden="true">
          {active ? "−" : "+"}
        </span>
      </button>
      <div className={styles.menu} id={`${facet.id}-range-menu`} aria-expanded={active}>
        <div className={styles.inputs}>
          <input
            type="number"
            className={styles.input}
            value={minInput}
            min={min}
            max={max}
            onInput={e => setMinInput((e.target as HTMLInputElement).value)}
            onBlur={commitMin}
            onKeyDown={e => {
              if (e.key === "Enter") {
                e.preventDefault()
                commitMin()
              }
            }}
            aria-label={`Minimum ${facet.name}`}
          />
          <input
            type="number"
            className={styles.input}
            value={maxInput}
            min={min}
            max={max}
            onInput={e => setMaxInput((e.target as HTMLInputElement).value)}
            onBlur={commitMax}
            onKeyDown={e => {
              if (e.key === "Enter") {
                e.preventDefault()
                commitMax()
              }
            }}
            aria-label={`Maximum ${facet.name}`}
          />
        </div>
        <div className={styles.slider}>
          <DualRange
            min={min}
            max={max}
            value={[range[0], range[1]]}
            onChange={updateRange}
            id={`ns-${facet.id}-range`}
            hideLabels
          />
        </div>
      </div>
    </li>
  )
}
